import { useTranslation } from '@rhino-project/core/i18n';
import { compact } from 'lodash-es';
import { useWatch } from 'react-hook-form';

import { useCallback, useEffect, useMemo } from 'react';
import {
  getIdentifierAttribute,
  getModelFromRef
} from '@rhino-project/core/utils';
import { useModelIndex } from '@rhino-project/core/hooks';
import {
  RhinoFilter,
  RhinoOrder,
  RhinoResource,
  RhinoResourceName
} from '@rhino-project/core';
import { filterPillText, useModelFilterField } from '../../../form';
import {
  useModelFiltersContext,
  useGlobalComponent
} from '@rhino-project/core/hooks';
import { FieldSelect } from '../../../FieldSelect';
import { ListBoxItem } from '@heroui/react';

/* The referenced model is resolved from the attribute at `path`, which only
 * happens at render, so a caller that wants its filter and order checked names
 * the target: <ModelFilterReferenceBase<'user'> path="reporter" ... />.
 * Unnamed, both stay as loose as they were. */
export interface ModelFilterReferenceProps<T = unknown> {
  path: string;
  filter?: T extends RhinoResourceName
    ? RhinoFilter<T>
    : Record<string, unknown>;
  limit?: number;
  offset?: number;
  order?: T extends RhinoResourceName ? RhinoOrder<T> : string[];
  search?: string;
  [key: string]: unknown;
}

export const ModelFilterReferenceBase = <T,>({
  path,
  filter,
  limit = 100,
  offset,
  order,
  search,
  ...props
}: ModelFilterReferenceProps<T>) => {
  const { t } = useTranslation();
  const { attribute, operator, plainPath, ...rest } = useModelFilterField(path);

  const refModel = useMemo(() => getModelFromRef(attribute), [attribute]);
  const identifier = useMemo(
    () => getIdentifierAttribute(refModel as RhinoResource),
    [refModel]
  );

  // We need to use an accessor here because the value can an object or the id
  const referenceAccessor = useCallback(
    // Rhino records carry the identifier/display_name at runtime, but the
    // generated record types don't surface those keys here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => value?.[identifier!.name] || value,
    [identifier]
  );

  const { isSuccess, results } = useModelIndex(refModel as RhinoResource, {
    filter,
    limit,
    offset,
    order,
    search
  });

  // We inject the ID because if we have both 'engagement.project.client'
  // and 'engagement.project' as filters, setting engagement.project.client will
  // cause engagement.project to have an object as a value
  const idPath = `${plainPath}.${identifier!.name}`;
  const fullPath = compact([idPath, operator]).join('.');

  const watch = useWatch({ name: fullPath });

  const { setPill } = useModelFiltersContext();

  useEffect(() => {
    if (isSuccess && watch !== undefined) {
      const resource = results!.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (r: any) => `${r[identifier!.name]}` === `${watch}`
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const displayName = (resource as any)?.display_name;

      setPill(
        fullPath,
        displayName ? filterPillText(attribute, null, displayName) : null
      );
    }
  }, [attribute, identifier, results, setPill, watch, isSuccess, fullPath]);

  // If there is no order, we sort by display_name
  // This has to be client side because display_name is not a sortable field in the database
  const sortedResults = useMemo(() => {
    if (order) return results;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (results as any[])?.sort((a, b) =>
      a?.display_name.localeCompare(b?.display_name, undefined, {
        sensitivity: 'base'
      })
    );
  }, [results, order]);

  return (
    <FieldSelect
      id={plainPath}
      path={fullPath}
      placeholder={t('rhino.frontend.messages.select_a_value', {
        value1: attribute.readableName.toLowerCase()
      })}
      accessor={referenceAccessor}
      {...(rest as Record<string, unknown>)}
      {...props}
    >
      {/* FIXME Should be a function with items */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {sortedResults?.map((result: any) => (
        <ListBoxItem
          key={result.id}
          id={result.id}
          textValue={result.display_name}
        >
          {result.display_name}
        </ListBoxItem>
      ))}
    </FieldSelect>
  );
};

export const ModelFilterReference = <T,>(props: ModelFilterReferenceProps<T>) =>
  useGlobalComponent('ModelFilterReference', ModelFilterReferenceBase, props);
